import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  cancelCashEntitlement,
  getCashEntitlementDisbursementLedger,
  returnCashEntitlementDisbursement,
} from "../../api/cashEntitlements";
import { mapCashEntitlementRow } from "../../utils/cashEntitlementMapper";
import {
  canCancelCashEntitlement,
  canRouteCashEntitlementDisbursement,
  cashEntitlementSourceLabel,
  cashEntitlementStatusMeta,
  formatCashAmount,
  formatCashDate,
} from "../../utils/cashEntitlement";
import "../advance/AdvanceDisbursementRouting.css";

function personLabel(person) {
  if (!person?.fullName) return "—";
  return `${person.fullName}${person.jobTitle ? ` (${person.jobTitle})` : ""}`;
}

function assigneeLabel(row) {
  if (row.disbursementAssignee?.fullName) {
    return personLabel(row.disbursementAssignee);
  }
  if (row.disbursementAssigneeLabel) return row.disbursementAssigneeLabel;
  if (
    row.disbursementTargetType === "CASHIER" &&
    row.disbursementStatus === "ASSIGNED"
  ) {
    return `كاشيرية فرع ${row.branchName || "الفرع"}`;
  }
  return "—";
}

export default function CashEntitlementLedger() {
  const { user } = useAuth();
  const canRoute = canRouteCashEntitlementDisbursement(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const payload = await getCashEntitlementDisbursementLedger();
      const list = (Array.isArray(payload) ? payload : payload?.items || []).map(
        mapCashEntitlementRow,
      );
      setRows(list);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تحميل سجل صرف المستحقات",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.employeeName,
        row.employeeCode,
        row.departmentName,
        row.branchName,
        row.reason,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const handleReturn = async (row) => {
    if (!canRoute || row.disbursementStatus !== "ASSIGNED") return;
    const ok = window.confirm(
      `سحب توجيه صرف ${row.employeeName || "الموظف"} وإعادته لقائمة التوجيه؟`,
    );
    if (!ok) return;
    setBusyId(row.id);
    try {
      await returnCashEntitlementDisbursement(row.id);
      await loadRows();
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر سحب التوجيه",
      );
    } finally {
      setBusyId("");
    }
  };

  const handleCancel = async (row) => {
    if (!canRoute || !canCancelCashEntitlement(row)) return;
    const ok = window.confirm(
      `إلغاء صرف مستحقات ${row.employeeName || "الموظف"} بمبلغ ${formatCashAmount(row.amount)}؟`,
    );
    if (!ok) return;
    setBusyId(row.id);
    try {
      await cancelCashEntitlement(row.id);
      await loadRows();
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر إلغاء الصرف",
      );
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="adv-disburse is-wide" dir="rtl">
      <div className="adv-disburse-header">
        <div>
          <h2 className="adv-disburse-title">سجل صرف المستحقات النقدية</h2>
          <p className="adv-disburse-subtitle">
            متابعة حالة التوجيه والتأكيد. الصرف النقدي لا يُخصم من المرتب.
          </p>
        </div>
        <button
          type="button"
          className="adv-disburse-refresh"
          onClick={loadRows}
          disabled={loading}
        >
          تحديث
        </button>
      </div>

      <div className="adv-disburse-toolbar">
        <input
          type="search"
          className="adv-disburse-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث بالاسم أو الكود..."
          disabled={loading}
        />
        <span className="adv-disburse-count">
          {filtered.length} من {rows.length}
        </span>
      </div>

      {loadError ? (
        <div className="adv-disburse-error-banner">{loadError}</div>
      ) : loading ? (
        <div className="adv-disburse-empty">جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="adv-disburse-empty">لا توجد مستحقات نقدية في السجل</div>
      ) : (
        <div className="adv-disburse-table-wrap">
          <table className="adv-disburse-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>المبلغ</th>
                <th>المصدر</th>
                <th>الحالة</th>
                <th>موجّه إلى</th>
                <th>تاريخ التأكيد</th>
                <th>أنشأه</th>
                {canRoute ? <th>إجراء</th> : null}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const status = cashEntitlementStatusMeta(row);
                return (
                  <tr key={row.id}>
                    <td className="is-name">
                      <div className="adv-disburse-name-cell">
                        <strong>{row.employeeName || "—"}</strong>
                        <span className="adv-disburse-reason-hint">
                          {row.employeeCode || ""}
                          {row.reason ? ` · ${row.reason}` : ""}
                        </span>
                      </div>
                    </td>
                    <td>{formatCashAmount(row.amount)}</td>
                    <td>{cashEntitlementSourceLabel(row.source)}</td>
                    <td>
                      <span className={`adv-disburse-badge ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>{assigneeLabel(row)}</td>
                    <td>{formatCashDate(row.disbursementConfirmedAt)}</td>
                    <td>{row.createdByName || "—"}</td>
                    {canRoute ? (
                      <td>
                        <div className="adv-row-actions">
                          {row.disbursementStatus === "ASSIGNED" &&
                          !row.cancelledAt ? (
                            <button
                              type="button"
                              className="adv-disburse-btn compact"
                              onClick={() => handleReturn(row)}
                              disabled={Boolean(busyId)}
                            >
                              سحب التوجيه
                            </button>
                          ) : null}
                          {canCancelCashEntitlement(row) ? (
                            <button
                              type="button"
                              className="adv-disburse-btn compact danger"
                              onClick={() => handleCancel(row)}
                              disabled={Boolean(busyId)}
                            >
                              إلغاء
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
